<script setup>
import { usePreSportsStore } from "@/stores/pre_sports";

defineProps({
	boost: {
		type: Object,
		required: true
	}
});

const preSportsStore = usePreSportsStore();
</script>

<template>
	<div class="bet-booster-card-body">
		<!-- Multi-leg match bet timeline -->
		<div v-if="boost.type === 'match' && boost.legs && boost.legs.length" class="legs-timeline">
			<div v-for="(leg, index) in boost.legs" :key="leg.id || index" class="timeline-item" :class="{ 'is-last': index === boost.legs.length - 1 }">
				<!-- Vertical Connector & Node Dot -->
				<div class="node-wrapper">
					<div class="node-dot"></div>
					<div v-if="index < boost.legs.length - 1" class="vertical-dashed-line"></div>
				</div>

				<!-- Leg Content -->
				<div class="leg-info">
					<div class="leg-title-row">
						<span class="leg-title" :title="leg.title">{{ leg.title }}</span>
						<!-- <span v-if="leg.badge" class="leg-badge">{{ leg.badge }}</span> -->
					</div>
					<div class="leg-market-row">
						<span class="leg-market" :title="leg.market">{{ leg.market }}</span>
						<span v-if="leg.icon === 'rocket'" class="rocket-icon">🚀</span>
						<span v-else-if="leg.icon === 'timer'" class="timer-icon">⏱️</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Match preview body when legs are not yet populated -->
		<div v-else-if="boost.type === 'match'" class="match-preview-body">
			<div class="match-preview-inner">
				<div class="match-teams-box">
					<span class="team-name home" :title="boost.team1?.name">{{ boost.team1?.name }}</span>
					<span class="vs-badge">VS</span>
					<span class="team-name away" :title="boost.team2?.name">{{ boost.team2?.name }}</span>
				</div>
				<div v-if="boost.competition_name || boost.competition" class="competition-tag">
					{{ boost.competition_name || boost.competition }}
				</div>
			</div>
		</div>

		<!-- Outright / Special Bet format -->
		<div v-else-if="boost.type === 'outright' || boost.type === 'special'" class="outright-body">
			<div v-for="(leg, index) in (boost.legs || [])" :key="leg.id || index" class="outright-item">
				<div class="outright-main-row">
					<div class="outright-sport-badge">
						<svg
							v-if="boost.sport_id && preSportsStore.preSportWhiteIcon"
							class="outright-sport-svg"
							xmlns="http://www.w3.org/2000/svg"
							xml:space="preserve"
							version="1.1"
							viewBox="0 0 30 25"
							xmlns:xlink="http://www.w3.org/1999/xlink"
							style="stroke: var(--dark);"
						>
							<use :xlink:href="preSportsStore.preSportWhiteIcon(boost.sport_id) + '#svg3108'" stroke-width="1.2px" />
						</svg>
						<span v-else class="outright-sport-fallback">🎯</span>
					</div>

					<span class="outright-participant" :title="leg.title">{{ leg.title }}</span>
				</div>
				<div class="outright-market-info" :title="leg.market">
					{{ leg.market }}
				</div>
				<div v-if="boost.competition" class="outright-competition" :title="boost.competition">
					{{ boost.competition }}
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.bet-booster-card-body {
	flex: 1;
	padding: 6px 12px 8px 12px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	min-height: 52px;
}

/* Match Preview Box (Graceful fallback while building legs) */
.match-preview-body {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;

	.match-preview-inner {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
		gap: 6px;

		.match-teams-box {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
			width: 100%;

			.team-name {
				font-size: 12.5px;
				font-weight: 700;
				color: var(--white);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				max-width: 42%;
				text-align: center;
			}

			.vs-badge {
				font-size: 9.5px;
				font-weight: 800;
				color: var(--white); font-weight: 700;
				background: rgba(255, 255, 255, 0.06);
				padding: 2px 5px;
				border-radius: 4px;
				letter-spacing: 0.5px;
			}
		}

		.competition-tag {
			font-size: 10.5px;
			color: rgba(255, 255, 255, 0.7);
			font-weight: 500;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			max-width: 90%;
		}
	}
}

/* Timeline Layout */
.legs-timeline {
	display: flex;
	flex-direction: column;

	.timeline-item {
		display: flex;
		align-items: flex-start;
		gap: 7px;
		position: relative;
		min-height: 32px;

		&.is-last {
			min-height: auto;
		}

		.node-wrapper {
			display: flex;
			flex-direction: column;
			align-items: center;
			width: 12px;
			flex-shrink: 0;
			padding-top: 3px;
			align-self: stretch;

			.node-dot {
				width: 7px;
				height: 7px;
				border-radius: 50%;
				border: 1.5px solid var(--primary);
				background: var(--primary);
				flex-shrink: 0;
				z-index: 1;
			}

			.vertical-dashed-line {
				width: 0;
				flex: 1;
				border-left: 1.5px dashed var(--primary);
				opacity: 0.65;
				margin-top: 1px;
				margin-bottom: -2px;
			}
		}

		.leg-info {
			flex: 1;
			min-width: 0;
			padding-bottom: 3px;

			.leg-title-row {
				display: flex;
				align-items: center;
				gap: 4px;

				.leg-title {
					font-size: 11.5px;
					font-weight: 700;
					color: var(--white);
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					line-height: 1.25;
				}

				.leg-badge {
					font-size: 9px;
					padding: 0 4px;
					background: var(--primary);
					border-radius: 3px;
					color: var(--white); font-weight: 700;
				}
			}

			.leg-market-row {
				display: flex;
				align-items: center;
				gap: 4px;

				.leg-market {
					font-size: 10.5px;
					color: var(--white);
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					line-height: 1.2;
				}

				.rocket-icon {
					font-size: 9.5px;
					flex-shrink: 0;
				}

				.timer-icon {
					font-size: 9.5px;
					flex-shrink: 0;
				}
			}
		}
	}
}

/* Outright / Special Layout */
.outright-body {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 6px;

	.outright-item {
		display: flex;
		flex-direction: column;
		gap: 3px;

		.outright-main-row {
			display: flex;
			align-items: center;
			gap: 8px;

			.outright-sport-badge {
				width: 22px;
				height: 22px;
				border-radius: 50%;
				background: transparent;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;

				.outright-sport-svg {
					width: 18px;
					height: 18px;
					fill: none;
					stroke: var(--white);
					display: block;
				}

				.outright-sport-fallback {
					font-size: 11px;
					line-height: 1;
				}
			}

			.outright-participant {
				font-size: 12px;
				font-weight: 700;
				color: var(--white);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}

		.outright-market-info {
			font-size: 10.5px;
			color: var(--white);
			line-height: 1.3;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			padding-left: 30px;
		}

		.outright-competition {
			font-size: 10px;
			color: var(--white);
			line-height: 1.2;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			padding-left: 30px;
		}
	}
}

@media (max-width: 991px) {
	.bet-booster-card-body {
		padding: 4px 10px 6px 10px;
		min-height: 44px;
	}

	.match-preview-body {
		.match-preview-inner {
			gap: 3px;

			.match-teams-box {
				gap: 5px;

				.team-name {
					font-size: 11px;
				}

				.vs-badge {
					font-size: 8.5px;
					padding: 1px 4px;
				}
			}

			.competition-tag {
				font-size: 9px;
			}
		}
	}

	.legs-timeline {
		.timeline-item {
			min-height: unset;
			gap: 6px;

			.node-wrapper {
				width: 10px;
				padding-top: 3px;

				.node-dot {
					width: 6px;
					height: 6px;
				}
			}

			.leg-info {
				padding-bottom: 2px;

				.leg-title-row {
					.leg-title {
						font-size: 11px;
					}

					.leg-badge {
						font-size: 8px;
					}
				}

				.leg-market-row {
					.leg-market {
						font-size: 10px;
					}

					.rocket-icon,
					.timer-icon {
						font-size: 8.5px;
					}
				}
			}
		}
	}

	.outright-body {
		gap: 4px;

		.outright-item {
			.outright-main-row {
				gap: 6px;

				.outright-sport-badge {
					width: 18px;
					height: 18px;

					.outright-sport-svg {
						width: 14px;
						height: 14px;
					}
				}

				.outright-participant {
					font-size: 11px;
				}
			}

			.outright-market-info {
				font-size: 9.5px;
				padding-left: 24px;
			}

			.outright-competition {
				font-size: 8.5px;
				padding-left: 24px;
			}
		}
	}
}

@media (max-width: 425px) {
	.bet-booster-card-body {
		padding: 4px 10px 6px 10px;
		min-height: unset;
	}
}
</style>
